import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, CircularProgress, Alert, Paper, FormControl, InputLabel
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';
import { generateToken } from '../utils/token';
import { loadCanvasQrCode } from '../utils/envelope';
import { downloadTextAsPdfFromBackend } from '../utils/pdf';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { buildApiUrl } from '../config/api';

function AdminQrCode() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState('');
    const [selectedSentence, setSelectedSentence] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [qrUrl, setQrUrl] = useState('');
    const [qrImage, setQrImage] = useState(null);
    const [sentenceCache, setSentenceCache] = useState({});
    const [randomMode, setRandomMode] = useState(true);

    useEffect(() => {
        if (!token) {
            navigate('/admin/login');
        }
    }, [token, navigate]);

    // Mover fetchParticipants para o escopo do componente
    const fetchParticipants = async () => {
        try {
            const response = await fetch(buildApiUrl('admin/participants'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar participantes');
            const data = await response.json();
            setParticipants(data);
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        if (token) {
            fetchParticipants();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (error && error.toLowerCase().includes('could not validate credentials')) {
            navigate('/admin/login');
        }
    }, [error, navigate]);

    const handleGenerateQr = async () => {
        setError('');
        setQrImage(null);
        setQrUrl('');
        let sentenceId = selectedSentence;
        let sentenceText = sentenceCache[selectedSentence];
        if (randomMode) {
            if (!selectedParticipant) {
                setError('Selecione um participante antes de gerar uma sentença aleatória.');
                return;
            }
            // Busca sentença aleatória do backend
            try {
                setIsLoading(true);
                const resp = await fetch(buildApiUrl(`admin/sentence/random?participant_id=${selectedParticipant}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!resp.ok) {
                    let msg = 'Erro ao buscar sentença aleatória';
                    try {
                        const errData = await resp.json();
                        if (errData && errData.detail) msg = errData.detail;
                    } catch {}
                    throw new Error(msg);
                }
                const data = await resp.json();
                sentenceId = data.iteration_id;
                sentenceText = data.paragraph;
                setSelectedSentence(sentenceId);
                setSentenceCache(prev => ({ ...prev, [sentenceId]: sentenceText }));
            } catch (err) {
                setError(err.message);
                setIsLoading(false);
                return;
            }
        }
        if (!selectedParticipant || !sentenceId) {
            setError('Selecione um participante e uma sentença.');
            return;
        }
        setIsLoading(true);
        try {
            // 1. Verifica se a sentença existe antes de registrar o QR code
            // Se já temos o texto (modo aleatório), não faz nova requisição
            if (!sentenceText) {
                if (!randomMode) {
                    const resp = await fetch(buildApiUrl(`admin/sentence/${sentenceId}`), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!resp.ok) throw new Error('Sentença não localizada. Informe um código válido.');
                    const data = await resp.json();
                    sentenceText = data.paragraph;
                    setSentenceCache(prev => ({ ...prev, [sentenceId]: sentenceText }));
                } else {
                    setError('Erro ao obter o texto da sentença sorteada. Tente novamente.');
                    setIsLoading(false);
                    return;
                }
            }
            // 2. Registra o QR code no backend
            console.log('Registrando QR code para participante:', selectedParticipant, 'e sentença:', sentenceId);
            console.log('Texto da sentença:', sentenceText);
            const regResp = await fetch(buildApiUrl('admin/qrcode/register'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    envelope_id: parseInt(sentenceId, 10),
                    participant_id: parseInt(selectedParticipant, 10),
                    sentence_text: sentenceText
                })
            });
            if (!regResp.ok) {
                let msg = 'Erro ao registrar QR code no banco';
                try {
                    const errData = await regResp.json();
                    if (errData && errData.detail && errData.detail.includes('sentença já foi utilizada')) {
                        msg = 'Já existe um envelope com essa sentença para esse participante. Escolha outra ou reimprima o envelope usando o menu de participantes.';
                    } else if (errData && errData.detail) {
                        msg = errData.detail;
                    }
                } catch {}
                throw new Error(msg);
            }
            // Atualiza a lista de participantes após registrar envelope
            await fetchParticipants();
            // Pega o UUID retornado pelo backend
            const regData = await regResp.json();
            // 3. Gera o token normalmente
            const expToken = generateToken(selectedParticipant, sentenceId);
            const baseUrl = window.location.origin;
            const basename = process.env.PUBLIC_URL || '';
            // 4. O link agora usa apenas o UUID (com basename para QA)
            const url = `${baseUrl}${basename}/welcome/${regData.uuid}?token=${expToken}`;
            setQrUrl(url);
            // 5. Gera o QR code normalmente
            const response = await fetch(buildApiUrl(`admin/qrcode?url=${encodeURIComponent(url)}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao gerar QR code');
            const blob = await response.blob();
            setQrImage(URL.createObjectURL(blob));
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };



    // Função para baixar QR code com label incorporado
    const handleDownloadQrWithLabel = async () => {
        if (!qrImage || !selectedParticipant || !qrUrl) return;
        const participant = participants.find(p => p.id === selectedParticipant);
        loadCanvasQrCode(qrImage, qrUrl, participant);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>Envelope</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={randomMode}
                            onChange={e => setRandomMode(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Modo aleatório"
                    sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="participant-select-label">Participante</InputLabel>
                    <Select
                        labelId="participant-select-label"
                        value={selectedParticipant}
                        label="Participante"
                        onChange={e => setSelectedParticipant(e.target.value)}
                    >
                        {participants.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.nickname}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel htmlFor="sentence-input"></InputLabel>
                    <input
                        id="sentence-input"
                        type="number"
                        min="1"
                        value={selectedSentence}
                        onChange={e => setSelectedSentence(e.target.value)}
                        style={{ width: '100%', padding: '16.5px 14px', fontSize: '16px', borderRadius: '4px', border: '1px solid #c4c4c4', boxSizing: 'border-box' }}
                        placeholder="Digite o ID da sentença"
                        disabled={randomMode}
                    />
                </FormControl>
                <Button variant="contained" onClick={handleGenerateQr} disabled={isLoading || !selectedParticipant || (!selectedSentence && !randomMode)}>
                    Gerar QR Code
                </Button>
            </Paper>
            {isLoading && <CircularProgress />}
            {qrImage && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                    <Typography variant="subtitle1">URL: {qrUrl}</Typography>
                    <img src={qrImage} alt="QR Code" style={{ marginTop: 16, maxWidth: 256 }} />
                    {/* Label SUBJID e UUID */}
                    {selectedParticipant && qrUrl && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {(() => {
                                const selected = participants.find(p => p.id === selectedParticipant);
                                const subjId = selected ? selected.nickname : selectedParticipant;
                                return `${subjId} | ${qrUrl.split('/').pop().split('-')[0]}`;
                            })()}
                        </Typography>
                    )}
                    {/* Botão de download do QR Code com label incorporado */}
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleDownloadQrWithLabel}
                        sx={{ mt: 2 }}
                    >
                        Baixar QR Code
                    </Button>
                    {/* Botão de download do texto em PDF */}
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={async () => {
                            // Extrai o uuid completo do qrUrl
                            let uuidFull = '';
                            if (qrUrl) {
                                const match = qrUrl.match(/([0-9a-fA-F-]{36})/);
                                uuidFull = match ? match[1] : '';
                            }
                            if (!uuidFull) {
                                alert('UUID do envelope não encontrado. Gere o QR code primeiro.');
                                return;
                            }
                            downloadTextAsPdfFromBackend(uuidFull);
                        }}
                    >
                        Baixar Texto em PDF
                    </Button>
                </Box>
            )}
        </Box>
    );
}

function printTextContent(sentence, uuid) {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
        <html>
        <head>
            <title>Imprimir Sentença</title>
            <style>
                @media print {
                    body { margin: 0; padding: 30mm 20mm 20mm 20mm; font-size: 19px; width: 210mm; height: 297mm; box-sizing: 'border-box'; }
                    .footer { position: fixed; bottom: 20mm; left: 0; width: 100%; text-align: center; font-size: 9px; color: #888; }
                }
                body { font-size: 19px; min-height: 250mm; word-break: break-word; }
                .footer { position: fixed; bottom: 20mm; left: 0; width: 100%; text-align: center; font-size: 9px; color: #888; }
            </style>
        </head>
        <body>
            <div>${sentence}</div>
            <div class="footer">${uuid}</div>
        </body>
        </html>
    `);
    printWindow.document.close();
    printWindow.print();
}

export { printTextContent };
export default AdminQrCode;

